import { Capacitor } from "@capacitor/core";
import { getBileniaWebAppUrl } from "@/lib/native/openBileniaWeb";
import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";
import { BileniaCalendar } from "@/lib/native/bileniaCalendar";
import { saveBlobDocument } from "@/lib/native/saveBlob";
import { isIOS } from "@/utils/platformUtils";
import {
  AuctionCalendarEvent,
  buildAuctionCalendarDescription,
  buildAuctionCalendarFileName,
  buildAuctionEndIcs,
  formatCalendarEventTitle,
} from "@/utils/calendarUtils";
import { buildAuctionListingPath } from "@/lib/listingUrl";

/** Safari (desktop or iOS) — blob `download` is unreliable; Calendar opens via navigation. */
function isSafariWeb(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /Safari/i.test(ua) && !/Chrome|CriOS|Chromium|Edg|Android/i.test(ua);
}

function downloadIcsFallback(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Open .ics so Apple Calendar can offer “Add Event”.
 *
 * Modern iOS Safari blocks `data:text/calendar` in hidden iframes (silent no-op),
 * which caused a success toast with no Calendar UI. Prefer a blob URL with mime
 * `text/calendar` (no charset — Safari is picky) and open it in a new context.
 */
function openIcsForAppleWeb(ics: string, fileName: string): void {
  // Do not include charset=utf-8 — iOS Safari often refuses that blob type.
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);

  const revokeLater = () => {
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.rel = "noopener";
    // Keep download for desktop Safari; iOS ignores it and still navigates/opens.
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    revokeLater();
    return;
  } catch (error) {
    console.warn("ICS blob download failed", error);
  }

  try {
    const opened = window.open(url, "_blank");
    if (opened) {
      revokeLater();
      return;
    }
  } catch (error) {
    console.warn("ICS window.open failed", error);
  }

  // Last resort — may leave the SPA; better than a silent no-op.
  window.location.assign(url);
  revokeLater();
}

function resolveListingUrl(event: AuctionCalendarEvent): string {
  if (event.listingId) {
    const path = buildAuctionListingPath(null, {
      id: event.listingId,
      slug: event.listingSlug,
    });
    return isNativeCapacitorApp()
      ? getBileniaWebAppUrl(path)
      : `${window.location.origin}${path}`;
  }
  return typeof window !== "undefined"
    ? window.location.href
    : getBileniaWebAppUrl();
}

function abortAsCancelled(): never {
  const error = new Error("Calendar add cancelled");
  error.name = "AbortError";
  throw error;
}

/**
 * Native iOS: EventKitUI editor (no .ics download / Quick Look).
 * Same event fields as the ICS we generate for other platforms.
 */
async function addAuctionToNativeIosCalendar(
  event: AuctionCalendarEvent,
  listingUrl: string,
): Promise<void> {
  const end = new Date(event.endTime);
  if (Number.isNaN(end.getTime())) {
    throw new Error("Invalid auction end time");
  }
  const start = new Date(end.getTime() - 30 * 60 * 1000);
  const language = event.language ?? "sv";
  const notes = buildAuctionCalendarDescription(event, listingUrl, language);

  const result = await BileniaCalendar.addCalendarEvent({
    title: formatCalendarEventTitle(event.title),
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    notes,
    url: listingUrl,
    reminderMinutesBefore: 60,
  });

  if (result.action === "cancelled" || result.action === "deleted") {
    abortAsCancelled();
  }
}

export async function addAuctionToCalendar(
  event: AuctionCalendarEvent,
): Promise<void> {
  const listingUrl = resolveListingUrl(event);

  const nativeIos =
    isNativeCapacitorApp() && Capacitor.getPlatform() === "ios";

  // Native iPhone/iPad: Apple Add Event UI — never save/preview .ics in Files.
  if (nativeIos) {
    await addAuctionToNativeIosCalendar(event, listingUrl);
    return;
  }

  const ics = buildAuctionEndIcs(event, listingUrl);
  const blob = new Blob([ics], { type: "text/calendar" });
  const fileName = buildAuctionCalendarFileName(event);
  const appleWeb = !isNativeCapacitorApp() && (isIOS() || isSafariWeb());

  // Safari / iPhone Safari (web): open ICS for Calendar — never Web Share.
  if (appleWeb) {
    openIcsForAppleWeb(ics, fileName);
    return;
  }

  // Native Android: save + “Open with…”.
  if (isNativeCapacitorApp()) {
    try {
      await saveBlobDocument(blob, fileName, {
        open: true,
        toast: false,
      });
      return;
    } catch (error) {
      console.warn("Native calendar save/open failed, using anchor download", error);
      downloadIcsFallback(blob, fileName);
      return;
    }
  }

  // Desktop Chrome etc.: download .ics.
  try {
    await saveBlobDocument(blob, fileName);
  } catch (error) {
    console.warn("saveBlobDocument failed, using anchor download", error);
    downloadIcsFallback(blob, fileName);
  }
}
